import React, { useState } from "react";
import numeral from "numeral";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  AssociatedCampaignWithRelations,
  useUpdateAssociatedCampaignGoalMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";

type RealInvestmentCellProps = {
  briefingId: string;
  campaign: AssociatedCampaignWithRelations;
  formatCurrency: (value: number, currency?: string) => string;
};

/**
 * Inversión real de una campaña asociada.
 * - Conectada y con coste de la API (> 0) → valor de solo lectura.
 * - Si no → input para meter el real a mano (goal.realPlatformInversion).
 *   Se guarda con el botón o con Enter.
 */
const RealInvestmentCell: React.FC<RealInvestmentCellProps> = ({
  briefingId,
  campaign,
  formatCurrency,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [updateGoal, { isLoading }] = useUpdateAssociatedCampaignGoalMutation();
  // undefined = sin editar; se muestra el valor guardado
  const [draft, setDraft] = useState<string | undefined>(undefined);

  const currency = campaign.goal?.currency || "EUR";
  const apiRealCost = campaign.realMetrics?.campaignInvestment;
  // Una línea enlazada sin métricas devuelve 0: sin coste de la API, el real se mete a mano
  // (los informes usan el manual cuando la API no tiene datos).
  const hasApiData =
    !!campaign.internalCampaignId && apiRealCost != null && apiRealCost > 0;

  if (hasApiData) {
    return (
      <Tooltip
        title={t(
          "briefingsView.realFromApi",
          "Value fetched automatically from the platform API"
        )}
      >
        <Typography variant="body2" noWrap>
          {formatCurrency(apiRealCost as number, currency)}
        </Typography>
      </Tooltip>
    );
  }

  const saved = campaign.goal?.realPlatformInversion;

  const handleSave = async () => {
    if (draft === undefined) return;
    const parsed = numeral(draft).value();
    if (draft !== "" && (parsed === null || isNaN(parsed))) {
      dispatch(
        setToastMessage({
          message: t("briefingsView.realInvalid", "Enter a valid number."),
          severity: "error",
        })
      );
      return;
    }
    if (!campaign.goal) {
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.realNoGoal",
            "The real value can't be saved because there is no goal configured."
          ),
          severity: "error",
        })
      );
      return;
    }
    try {
      await updateGoal({
        params: { briefingId, associatedId: campaign.id },
        body: { realPlatformInversion: draft === "" ? null : parsed },
      }).unwrap();
      setDraft(undefined);
      dispatch(
        setToastMessage({
          message: t("briefingsView.realSaved", "Real value saved."),
          severity: "success",
        })
      );
    } catch (error) {
      console.error("Failed to update real platform inversion:", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.realSaveError",
            "Unable to save the real value."
          ),
          severity: "error",
        })
      );
    }
  };

  return (
    <TextField
      size="small"
      placeholder="0,00"
      value={draft !== undefined ? draft : (saved ?? "")}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSave();
        if (e.key === "Escape") setDraft(undefined);
      }}
      // La fila abre la campaña con doble clic; aquí solo se edita.
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      disabled={isLoading}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {isLoading ? (
              <CircularProgress size={16} />
            ) : draft !== undefined ? (
              <IconButton
                size="small"
                color="primary"
                edge="end"
                onClick={handleSave}
                aria-label={t("common.save", "Save")}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {currency}
              </Typography>
            )}
          </InputAdornment>
        ),
      }}
      sx={{ width: 150 }}
    />
  );
};

export default RealInvestmentCell;
